// Onglet « Vérifications externes » côté bailleur : confirmation employeur (F-024), Mes droits sociaux (F-025), identité (F-031).
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, today } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, ErrorBox, Input, Loading, Notice, Select, StatusChip, Textarea, fieldError, type CheckStatus } from "@core/ui";
import { CheckLine, OfficialLink, ROLE_LABELS, docUrl, fullName, useAction, type Check, type Person } from "./shared";

export type EmployerRequest = {
  id: string;
  applicant_id: string;
  applicant_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  contact_source: "candidat" | "bailleur";
  channel_verification: string;
  status: string;
  consent_at: string | null;
  refused_at: string | null;
  sent_at: string | null;
  expires_at: string | null;
  reminder_count: number;
  responded_at: string | null;
  respondent_name: string | null;
  respondent_role: string | null;
  employer_declined: boolean | null;
  answers: { in_post?: string; contract_type?: string; trial_period_over?: string; notice_in_progress?: string; declines?: boolean };
};
type Social = { id: string; applicant_id: string; applicant_name: string; status: string; document_id: string | null; period_start: string | null; period_end: string | null; result: string | null; verified_on: string | null; verified_by_name: string | null; note: string };
type Identity = {
  id: string;
  applicant_id: string;
  applicant_name: string;
  method: "france_identite" | "piece_classique";
  document_kind: string | null;
  foreign_document: boolean;
  status: string;
  document_id: string | null;
  document_result: string | null;
  valid_until: string | null;
  recipient_matches: boolean | null;
  holder_name: string | null;
  name_matches: boolean | null;
  link_result: string | null;
  verified_by_name: string | null;
  verified_at: string | null;
};
type Doc = { id: string; applicant_id: string | null; category: string; title: string; filename: string };
type View = {
  application: { id: string; status: string; org_name: string };
  applicants: Person[];
  employer_confirmations: EmployerRequest[];
  social_rights: Social[];
  identity_checks: Identity[];
  documents: Doc[];
  checks: Check[];
  links: { mes_droits_sociaux: string; mes_droits_sociaux_aide: string; france_identite: string; france_identite_aide: string };
  alternatives: string;
  document_kinds: { value: string; label: string }[];
};

export const EMPLOYER_STATUS: Record<string, { label: string; status: CheckStatus }> = {
  attente_consentement: { label: "En attente de l’accord du candidat", status: "a_confirmer" },
  prete: { label: "Accord donné, prête à envoyer", status: "info" },
  envoyee: { label: "Envoyée, en attente de réponse", status: "a_confirmer" },
  repondue: { label: "Réponse reçue", status: "conforme" },
  expiree: { label: "Expirée sans réponse", status: "non_concluant" },
  refusee_candidat: { label: "Refusée par le candidat", status: "info" },
  annulee: { label: "Annulée", status: "info" },
};

const TRI: Record<string, string> = { oui: "oui", non: "non", ne_sait_pas: "ne sait pas", sans_objet: "sans objet" };
const CONTRACTS: Record<string, string> = {
  cdi: "CDI",
  cdd: "CDD",
  interim: "Intérim",
  fonction_publique: "Fonction publique",
  apprentissage: "Apprentissage ou alternance",
  autre: "Autre",
  ne_souhaite_pas_repondre: "Ne souhaite pas répondre",
};

export function EmployerAnswers({ r }: { r: EmployerRequest }) {
  if (!r.responded_at) return null;
  if (r.employer_declined) return <p className="small">L’interlocuteur n’a pas souhaité répondre ({dateTime(r.responded_at)}).</p>;
  const a = r.answers;
  return (
    <ul className="small decision-reponses">
      <li>En poste à ce jour : {TRI[a.in_post ?? ""] ?? "—"}</li>
      <li>Type de contrat : {CONTRACTS[a.contract_type ?? ""] ?? "—"}</li>
      <li>Période d’essai terminée : {TRI[a.trial_period_over ?? ""] ?? "—"}</li>
      <li>Préavis en cours : {TRI[a.notice_in_progress ?? ""] ?? "—"}</li>
      <li>
        Répondant déclaré : {r.respondent_name} ({r.respondent_role}), le {dateTime(r.responded_at)}
      </li>
    </ul>
  );
}

const DOC_RESULTS_FI = [
  { value: "valide", label: "Signature, validité et destinataire vérifiés sur le site officiel" },
  { value: "invalide", label: "Non reconnu par le vérificateur officiel" },
  { value: "non_verifiable", label: "Vérification impossible" },
];
const DOC_RESULTS_CLASSIC = [
  { value: "coherente", label: "Mentions lisibles, cohérentes et en cours de validité" },
  { value: "incoherente", label: "Mentions illisibles, incomplètes ou contradictoires" },
  { value: "non_verifiable", label: "Examen impossible à distance" },
];
const LINK_RESULTS = [
  { value: "a_verifier", label: "À vérifier (par exemple original présenté lors de la visite)" },
  { value: "concordant", label: "La pièce correspond à la personne qui candidate" },
  { value: "divergent", label: "La pièce ne correspond pas à la personne qui candidate" },
  { value: "non_verifiable", label: "Rapprochement impossible" },
];

export function VerificationsTab({ orgId, applicationId }: { orgId: string; applicationId: string }) {
  const org = useOrg();
  const base = `/api/v1/orgs/${orgId}/applications/${applicationId}`;
  const q = useGet<View>(`${base}/external-verifications`);
  const prefixes = [base, `/api/v1/orgs/${orgId}/decision-summary`];
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const can = org.can("operate");
  return (
    <div className="stack">
      <EmployerCard v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
      <SocialCard v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
      <IdentityCard v={v} orgId={orgId} base={base} prefixes={prefixes} can={can} />
      <Card title="Constats emploi et identité">
        {v.checks.length ? (
          <div className="stack">
            {v.checks.map((c) => (
              <CheckLine key={c.id} check={c} extra={<span className="small muted">{v.applicants.find((a) => a.id === c.applicant_id) ? fullName(v.applicants.find((a) => a.id === c.applicant_id)!) : ""}</span>} />
            ))}
          </div>
        ) : (
          <p className="muted">Aucun constat d’emploi ou d’identité pour l’instant.</p>
        )}
      </Card>
    </div>
  );
}

type CardProps = { v: View; orgId: string; base: string; prefixes: string[]; can: boolean };

function EmployerCard({ v, orgId, base, prefixes, can }: CardProps) {
  const tenants = v.applicants.filter((a) => a.role !== "garant");
  const [form, setForm] = useState({ applicant_id: tenants[0]?.id ?? "", contact_name: "", contact_email: "", contact_phone: "", channel_verification: "" });
  const action = useAction(prefixes);
  return (
    <Card title="Confirmation récente par l’employeur">
      <Notice>
        Rien n’est envoyé à l’employeur sans l’accord préalable du candidat. Vérifiez le contact par un canal indépendant (standard de l’entreprise, site officiel) : une adresse lue sur une pièce ne suffit pas. L’employeur ne reçoit que quatre questions ; le dossier ne lui est jamais transmis. Une absence de réponse signifie « emploi actuel non confirmé », jamais une conclusion défavorable.
      </Notice>
      {v.employer_confirmations.length ? (
        <div className="stack">
          {v.employer_confirmations.map((r) => (
            <EmployerItem key={r.id} r={r} orgId={orgId} prefixes={prefixes} can={can} alternatives={v.alternatives} />
          ))}
        </div>
      ) : (
        <p className="muted">Aucune demande de confirmation.</p>
      )}
      {can && tenants.length ? (
        <form
          className="stack decision-sous-formulaire"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run(
              "creer",
              () => post(`${base}/employer-confirmations`, { ...form, contact_phone: form.contact_phone || null, channel_verification: form.channel_verification || null }),
              "Demande préparée : le candidat est invité à donner son accord.",
            );
            if (ok) setForm({ ...form, contact_name: "", contact_email: "", contact_phone: "", channel_verification: "" });
          }}
        >
          <h3>Préparer une demande</h3>
          <div className="form-row">
            <Select label="Personne concernée" value={form.applicant_id} onChange={(e) => setForm({ ...form, applicant_id: e.target.value })} options={tenants.map((a) => ({ value: a.id, label: fullName(a) }))} />
            <Input label="Interlocuteur RH" required value={form.contact_name} error={fieldError(action.error, "contact_name")} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            <Input label="Adresse e-mail de l’interlocuteur" type="email" required value={form.contact_email} error={fieldError(action.error, "contact_email")} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            <Input label="Téléphone (facultatif)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <Textarea
            label="Comment avez-vous vérifié ce contact ? (facultatif)"
            help="Par exemple : standard trouvé sur le site de l’entreprise, service RH confirmé par téléphone. Sans vérification indépendante, la réponse restera « à confirmer »."
            value={form.channel_verification}
            onChange={(e) => setForm({ ...form, channel_verification: e.target.value })}
          />
          <div className="form-actions">
            <Button type="submit" variant="secondaire" busy={action.busy === "creer"}>
              Demander l’accord du candidat
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function EmployerItem({ r, orgId, prefixes, can, alternatives }: { r: EmployerRequest; orgId: string; prefixes: string[]; can: boolean; alternatives: string }) {
  const [verification, setVerification] = useState(r.channel_verification);
  const action = useAction(prefixes);
  const st = EMPLOYER_STATUS[r.status] ?? { label: r.status, status: "info" as CheckStatus };
  const url = `/api/v1/orgs/${orgId}/employer-confirmations/${r.id}`;
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>{r.applicant_name}</strong>
          <span className="small muted">
            {" "}
            · {r.contact_name} ({r.contact_email}) · contact fourni par le {r.contact_source}
          </span>
        </div>
        <StatusChip status={st.status} label={st.label} />
      </header>
      <p className="small muted">
        {r.consent_at ? `Accord du candidat le ${dateTime(r.consent_at)}. ` : ""}
        {r.sent_at ? `Envoyée le ${dateTime(r.sent_at)}. ` : ""}
        {r.expires_at && (r.status === "envoyee" || r.status === "expiree") ? `Lien valable jusqu’au ${dateTime(r.expires_at)}. ` : ""}
        {r.reminder_count ? `${r.reminder_count} relance(s). ` : ""}
        {r.channel_verification ? `Contact vérifié : ${r.channel_verification}` : "Contact non vérifié indépendamment."}
      </p>
      <EmployerAnswers r={r} />
      {r.status === "refusee_candidat" ? <Notice>{`Le candidat n’a pas donné son accord. Ce refus n’est pas un motif défavorable. ${alternatives}`}</Notice> : null}
      {can && r.status === "prete" ? (
        <div className="stack-sm">
          <Textarea label="Vérification du contact (recommandée)" value={verification} onChange={(e) => setVerification(e.target.value)} help="Décrivez comment vous avez vérifié ce contact indépendamment des coordonnées fournies." />
          <div className="form-actions">
            <Button small busy={action.busy === "envoyer"} onClick={() => action.run("envoyer", () => post(`${url}/send`, { channel_verification: verification || null }), "Demande envoyée à l’employeur (envoi simulé).")}>
              Envoyer le lien à l’employeur
            </Button>
            <Button variant="discret" small busy={action.busy === "annuler"} onClick={() => action.run("annuler", () => post(`${url}/cancel`, {}), "Demande annulée.")}>
              Annuler
            </Button>
          </div>
        </div>
      ) : null}
      {can && (r.status === "envoyee" || r.status === "expiree" || r.status === "attente_consentement") ? (
        <div className="form-actions">
          {r.status !== "attente_consentement" && r.reminder_count < 2 ? (
            <Button variant="secondaire" small busy={action.busy === "relancer"} onClick={() => action.run("relancer", () => post(`${url}/remind`, {}), "Relance envoyée avec un nouveau lien.")}>
              Relancer l’employeur
            </Button>
          ) : null}
          <Button variant="discret" small busy={action.busy === "annuler"} onClick={() => action.run("annuler", () => post(`${url}/cancel`, {}), "Demande annulée.")}>
            Annuler la demande
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function SocialCard({ v, orgId, base, prefixes, can }: CardProps) {
  const [applicant, setApplicant] = useState(v.applicants[0]?.id ?? "");
  const [documentId, setDocumentId] = useState("");
  const action = useAction(prefixes);
  const docs = v.documents.filter((d) => !applicant || d.applicant_id === applicant);
  return (
    <Card title="Attestation Mes droits sociaux">
      <p className="muted">
        Le candidat télécharge lui-même son attestation d’activité depuis <OfficialLink href={v.links.mes_droits_sociaux}>mesdroitssociaux.gouv.fr</OfficialLink> avec ses propres identifiants — Bailivo ne les demande jamais et n’accède à aucun portail. Les déclarations peuvent avoir un décalage : l’attestation ne confirme pas seule l’emploi à ce jour.{" "}
        <OfficialLink href={v.links.mes_droits_sociaux_aide}>Guide d’aide</OfficialLink>
      </p>
      {v.social_rights.length ? (
        <div className="stack">
          {v.social_rights.map((s) => (
            <SocialItem key={s.id} s={s} orgId={orgId} prefixes={prefixes} can={can} />
          ))}
        </div>
      ) : null}
      {can ? (
        <form
          className="form-row decision-sous-formulaire"
          onSubmit={async (e) => {
            e.preventDefault();
            await action.run("proposer", () => post(`${base}/social-rights`, { applicant_id: applicant, document_id: documentId || null }), documentId ? "Attestation rattachée : vous pouvez la vérifier." : "Parcours guidé envoyé au candidat.");
            setDocumentId("");
          }}
        >
          <Select label="Personne" value={applicant} onChange={(e) => setApplicant(e.target.value)} options={v.applicants.map((a) => ({ value: a.id, label: `${fullName(a)} (${ROLE_LABELS[a.role]})` }))} />
          <Select
            label="Attestation déjà reçue (facultatif)"
            value={documentId}
            onChange={(e) => setDocumentId(e.target.value)}
            options={[{ value: "", label: "Non : proposer le parcours guidé" }, ...docs.map((d) => ({ value: d.id, label: d.title || d.filename }))]}
          />
          <Button type="submit" variant="secondaire" busy={action.busy === "proposer"}>
            {documentId ? "Rattacher et vérifier" : "Proposer le parcours au candidat"}
          </Button>
        </form>
      ) : null}
      <ErrorBox error={action.error} />
    </Card>
  );
}

function SocialItem({ s, orgId, prefixes, can }: { s: Social; orgId: string; prefixes: string[]; can: boolean }) {
  const [form, setForm] = useState({ result: "concordant", period_start: s.period_start ?? "", period_end: s.period_end ?? "", verified_on: today(), note: "" });
  const action = useAction(prefixes);
  const label = s.status === "attente_depot" ? "En attente du dépôt" : s.status === "deposee" ? "Déposée, à vérifier" : "Vérifiée";
  return (
    <article className="decision-echange">
      <header className="row-between">
        <strong>{s.applicant_name}</strong>
        <StatusChip status={s.status === "verifiee" ? "conforme" : s.status === "deposee" ? "a_confirmer" : "manquant"} label={label} />
      </header>
      <p className="small muted">
        {s.document_id ? (
          <a href={docUrl(s.document_id)} target="_blank" rel="noreferrer">
            Ouvrir l’attestation
          </a>
        ) : (
          "Aucun fichier déposé."
        )}
        {s.period_start || s.period_end ? ` · Période : ${date(s.period_start)} → ${date(s.period_end)}` : ""}
        {s.verified_on ? ` · Vérifiée le ${date(s.verified_on)} par ${s.verified_by_name ?? "l’espace"}` : ""}
      </p>
      {can && s.status === "deposee" ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            action.run(
              "verifier",
              () => post(`/api/v1/orgs/${orgId}/social-rights/${s.id}/verify`, { ...form, period_start: form.period_start || null, period_end: form.period_end || null }),
              "Vérification enregistrée.",
            );
          }}
        >
          <div className="form-row">
            <Select
              label="Résultat de votre examen"
              value={form.result}
              onChange={(e) => setForm({ ...form, result: e.target.value })}
              options={[
                { value: "concordant", label: "Concorde avec les pièces d’emploi" },
                { value: "divergent", label: "Diffère des pièces d’emploi" },
                { value: "non_verifiable", label: "Non vérifiable" },
              ]}
            />
            <Input label="Début de la période" type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            <Input label="Fin de la période" type="date" value={form.period_end} error={fieldError(action.error, "period_end")} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            <Input label="Vérifiée le" type="date" value={form.verified_on} error={fieldError(action.error, "verified_on")} onChange={(e) => setForm({ ...form, verified_on: e.target.value })} />
          </div>
          <Input label="Note (facultative)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="form-actions">
            <Button type="submit" small busy={action.busy === "verifier"}>
              Enregistrer ma vérification
            </Button>
          </div>
        </form>
      ) : null}
    </article>
  );
}

function IdentityCard({ v, orgId, base, prefixes, can }: CardProps) {
  const [form, setForm] = useState({ applicant_id: v.applicants[0]?.id ?? "", method: "france_identite", foreign_document: false, document_id: "" });
  const action = useAction(prefixes);
  const docs = v.documents.filter((d) => d.applicant_id === form.applicant_id);
  return (
    <Card title="Identité : France Identité ou pièce classique">
      <p className="muted">
        Deux constats distincts sont produits : l’authenticité ou la validité du document, puis son rattachement à la personne qui candidate. Un document valide ne prouve pas à lui seul que le déposant en est le titulaire. Aucune biométrie ni comparaison de photographie. Le justificatif à usage unique se vérifie sur le{" "}
        <OfficialLink href={v.links.france_identite}>vérificateur officiel France Identité</OfficialLink>. Un candidat sans l’application ou avec un document étranger suit le parcours classique, tout aussi valable.
      </p>
      {v.identity_checks.length ? (
        <div className="stack">
          {v.identity_checks.map((i) => (
            <IdentityItem key={i.id} i={i} orgId={orgId} prefixes={prefixes} can={can} kinds={v.document_kinds} />
          ))}
        </div>
      ) : null}
      {can ? (
        <form
          className="stack-sm decision-sous-formulaire"
          onSubmit={(e) => {
            e.preventDefault();
            action.run("proposer", () => post(`${base}/identity-checks`, { ...form, document_id: form.document_id || null }), form.document_id ? "Pièce rattachée : vous pouvez l’examiner." : "Parcours proposé au candidat.");
          }}
        >
          <div className="form-row">
            <Select label="Personne" value={form.applicant_id} onChange={(e) => setForm({ ...form, applicant_id: e.target.value, document_id: "" })} options={v.applicants.map((a) => ({ value: a.id, label: `${fullName(a)} (${ROLE_LABELS[a.role]})` }))} />
            <Select
              label="Parcours"
              value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value, foreign_document: e.target.value === "france_identite" ? false : form.foreign_document })}
              options={[
                { value: "france_identite", label: "Justificatif à usage unique France Identité" },
                { value: "piece_classique", label: "Pièce d’identité classique" },
              ]}
            />
            <Select label="Pièce déjà reçue (facultatif)" value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} options={[{ value: "", label: "Non : proposer le parcours" }, ...docs.map((d) => ({ value: d.id, label: d.title || d.filename }))]} />
          </div>
          {form.method === "piece_classique" ? <Checkbox label="Document étranger" checked={form.foreign_document} onChange={(c) => setForm({ ...form, foreign_document: c })} /> : null}
          <div className="form-actions">
            <Button type="submit" variant="secondaire" busy={action.busy === "proposer"}>
              {form.document_id ? "Rattacher la pièce" : "Proposer le parcours"}
            </Button>
          </div>
          <ErrorBox error={action.error} />
        </form>
      ) : null}
    </Card>
  );
}

function IdentityItem({ i, orgId, prefixes, can, kinds }: { i: Identity; orgId: string; prefixes: string[]; can: boolean; kinds: { value: string; label: string }[] }) {
  const fi = i.method === "france_identite";
  const [form, setForm] = useState({ document_result: fi ? "valide" : "coherente", recipient_matches: "oui", valid_until: "", holder_name: "", link_result: "a_verifier", note: "" });
  const action = useAction(prefixes);
  const label = i.status === "attente_depot" ? "En attente du dépôt" : i.status === "deposee" ? "Déposée, à examiner" : "Examinée";
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>{i.applicant_name}</strong>
          <span className="small muted">
            {" "}
            · {fi ? "France Identité" : "Pièce classique"}
            {i.document_kind ? ` · ${kinds.find((k) => k.value === i.document_kind)?.label ?? i.document_kind}` : ""}
            {i.foreign_document ? " · document étranger" : ""}
          </span>
        </div>
        <StatusChip status={i.status === "verifiee" ? "conforme" : i.status === "deposee" ? "a_confirmer" : "manquant"} label={label} />
      </header>
      {i.document_id ? (
        <p className="small">
          <a href={docUrl(i.document_id)} target="_blank" rel="noreferrer">
            Ouvrir la pièce
          </a>
          {i.verified_at ? ` · examinée le ${dateTime(i.verified_at)} par ${i.verified_by_name ?? "l’espace"}` : ""}
          {i.name_matches === false ? " · le nom lu diffère du nom déclaré" : ""}
        </p>
      ) : null}
      {can && i.status === "deposee" ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            action.run(
              "examiner",
              () =>
                post(`/api/v1/orgs/${orgId}/identity-checks/${i.id}/verify`, {
                  document_result: form.document_result,
                  link_result: form.link_result,
                  valid_until: form.valid_until || null,
                  recipient_matches: fi && form.document_result !== "non_verifiable" ? form.recipient_matches === "oui" : null,
                  holder_name: form.holder_name || null,
                  note: form.note,
                }),
              "Examen enregistré : deux constats distincts ont été créés.",
            );
          }}
        >
          <div className="form-row">
            <Select label="1. Le document" value={form.document_result} onChange={(e) => setForm({ ...form, document_result: e.target.value })} options={fi ? DOC_RESULTS_FI : DOC_RESULTS_CLASSIC} />
            {fi ? (
              <Select
                label="Destinataire et motif"
                value={form.recipient_matches}
                error={fieldError(action.error, "recipient_matches")}
                onChange={(e) => setForm({ ...form, recipient_matches: e.target.value })}
                options={[
                  { value: "oui", label: "Établi pour vous et cette location" },
                  { value: "non", label: "Autre destinataire ou autre motif" },
                ]}
              />
            ) : null}
            <Input label="Valable jusqu’au" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            <Input label="Nom lu sur la pièce (facultatif)" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} />
          </div>
          <Select label="2. Rattachement à la personne qui candidate" value={form.link_result} onChange={(e) => setForm({ ...form, link_result: e.target.value })} options={LINK_RESULTS} />
          <Input label="Note (facultative)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="form-actions">
            <Button type="submit" small busy={action.busy === "examiner"}>
              Enregistrer mon examen
            </Button>
          </div>
        </form>
      ) : null}
    </article>
  );
}
